import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Blocos do sistema visual da App do Segurança.
 *
 * Segue as convenções do iOS (Settings/Inset-grouped) e do Android (Material):
 * o conteúdo vive em CARTÕES sobre um fundo cinzento, os campos são preenchidos
 * (não caixas com borda de 1px), e as listas separam-se por linhas finas em vez
 * de bordas à volta de cada item. É isto que faz um ecrã "saber" a app nativa em
 * vez de a formulário web.
 *
 * Não é decoração: um guarda com luvas, de noite, precisa de alvos grandes e de
 * hierarquia legível de relance.
 */

/** Vibração curta. É o que confirma um toque numa app nativa — sem esperar a rede. */
export const haptic = (pattern: number | number[] = 8) => {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* o browser pode não suportar; é um extra, nunca um requisito */
  }
};

/**
 * Título de secção — o "eyebrow" da casa.
 *
 * Era `text-[13px] font-semibold tracking-wide`, que destoava do sistema de
 * design (§1: eyebrows são 11px, `font-medium`, tracking muito aberto). Passa a
 * ser o mesmo em toda a app; no telemóvel só o tracking abre um pouco menos,
 * porque a 11px com 0.18em as palavras longas ("FOTOS E DOCUMENTOS") partiam.
 */
export function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2
      className={cn(
        "px-1 pb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground md:tracking-[0.18em]",
        className,
      )}
    >
      {children}
    </h2>
  );
}

/**
 * Cartão: o contentor de tudo.
 *
 * A sombra (`shadow-xs`) desaparece a favor da borda subtil do sistema (§1) —
 * a app fica com o mesmo relevo do resto da plataforma em vez de cartões a
 * flutuar. O raio encolhe em `md+`: 16px num cartão de 1200px de largura lê-se
 * como um balão; 12px é o `rounded-xl` que a casa usa em contentores.
 */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border/60 bg-card md:rounded-xl",
        "shadow-[0_1px_2px_rgba(0,0,0,0.03)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Linha de lista dentro de um cartão.
 *
 * Os separadores são desenhados entre linhas (`[&:not(:last-child)]`), não à
 * volta de cada item — é o que distingue uma lista nativa de uma pilha de
 * caixas. `onClick` torna a linha inteira tocável (alvo grande) e faz vibrar.
 */
export function Row({
  children,
  onClick,
  chevron,
  className,
  id,
}: {
  children: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
  className?: string;
  /** Âncora para o deep-link da pesquisa global levar a linha ao ecrã. */
  id?: string;
}) {
  // Telemóvel: 56px de alvo (dedo com luva, de noite). Desktop: 40px, que num
  // rato é folgado e deixa caber o dobro das linhas no ecrã sem rolar.
  const inner = (
    <div
      className={cn(
        "flex min-h-[56px] w-full items-center gap-3 px-4 py-3 text-left md:min-h-[40px] md:gap-2.5 md:px-3 md:py-2",
        className,
      )}
    >
      <div className="min-w-0 flex-1">{children}</div>
      {chevron && (
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60" strokeWidth={1.75} />
      )}
    </div>
  );

  if (!onClick) {
    return (
      <div id={id} className="scroll-mt-24 border-b border-border/60 last:border-b-0">
        {inner}
      </div>
    );
  }

  return (
    <button
      type="button"
      id={id}
      onClick={() => {
        haptic();
        onClick();
      }}
      // Hover NEUTRO no desktop (§1) — a cor primária é para acções, não para
      // assinalar que o rato passou por cima.
      className="w-full scroll-mt-24 border-b border-border/60 transition-colors last:border-b-0 active:bg-muted/60 md:hover:bg-foreground/[0.035]"
    >
      {inner}
    </button>
  );
}

/**
 * Campo de formulário ao estilo nativo: rótulo à esquerda, valor à direita,
 * dentro do cartão. Sem caixas — o `input` é transparente e a linha é a borda.
 */
export function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    // `@container/field`: o campo reage à largura do CARTÃO onde está (§5). O
    // mesmo `Field` vive num telemóvel de 390px e numa coluna de secretária de
    // 700px — um `lg:` aqui olhava para o ecrã, que não é quem o aperta.
    <div
      className={cn(
        "@container/field flex min-h-[56px] items-center gap-3 border-b border-border/60 px-4 last:border-b-0",
        "md:min-h-[38px] md:gap-2.5 md:px-3",
        className,
      )}
    >
      {/* O rótulo tinha largura FIXA (96px) em qualquer largura de cartão: numa
          coluna de 700px sobravam 600px para um campo que precisa de 300, e a
          linha lia-se como um formulário de telemóvel esticado. A partir de
          `@lg` (~512px de cartão) o rótulo cresce e o campo deixa de ser uma
          faixa perdida no meio do vazio. */}
      <span className="w-28 shrink-0 text-[15px] text-muted-foreground md:w-24 md:text-[13px] @lg/field:w-32">
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/**
 * Input de um `Field`.
 *
 * 16px no telemóvel é OBRIGATÓRIO e não se toca: abaixo disso o Safari do iOS
 * faz zoom ao tocar no campo e o ecrã salta. A partir de `md` não há iPhone —
 * cai para os 13px do corpo da casa, que é o que dá a densidade de secretária.
 */
export function FieldInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "w-full bg-transparent py-3 text-[16px] outline-hidden placeholder:text-muted-foreground/50",
        "md:py-1.5 md:text-[13px]",
        props.className,
      )}
    />
  );
}

/**
 * Botão de ação principal: alto e largo ao toque, compacto na secretária.
 *
 * O `success` deixou de ser um bloco verde maciço. Um ecrã com vários cartões
 * tinha uma parede de verde saturado a competir com o laranja da marca, e a
 * hierarquia achatava — tudo gritava ao mesmo tempo. A entrada/saída CONTINUA a
 * ler-se por cor (é semântica real, não decoração), mas agora como acento sobre
 * fundo neutro: o verde vive no ícone, no texto e na borda. O único bloco cheio
 * que resta no ecrã é o da acção primária de facto (`primary`).
 */
export function ActionButton({
  children,
  onClick,
  disabled,
  variant = "primary",
  type = "button",
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "success" | "neutral";
  type?: "button" | "submit";
  className?: string;
}) {
  const styles = {
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",
    success:
      "border border-emerald-600/30 bg-emerald-600/[0.07] text-emerald-700 dark:text-emerald-400 " +
      "hover:bg-emerald-600/12 [&_svg]:text-emerald-600 dark:[&_svg]:text-emerald-400",
    neutral: "border border-border bg-card text-foreground hover:bg-foreground/[0.035]",
  }[variant];

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={
        onClick
          ? () => {
              haptic();
              onClick();
            }
          : undefined
      }
      className={cn(
        // Telemóvel: 52px de alvo, 17px de texto — legível a um braço de
        // distância. Desktop: h-9 e 13px, as medidas de controlo da casa.
        "flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl text-[17px] font-semibold",
        "md:min-h-9 md:rounded-lg md:text-[13px] md:font-medium",
        "transition-all active:scale-[0.97] disabled:opacity-50 md:active:scale-100",
        "focus-visible:border-primary focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-primary/10",
        styles,
        className,
      )}
    >
      {children}
    </button>
  );
}
